import React from "react"
import { Row, Table } from "@tanstack/react-table"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { TableRowData, TableSize, TableStyle, TableVirtualizationConfig } from "../types"
import { TableBodyRow } from "./TableBodyRow"
import { TableBodyCell } from "./TableBodyCell"
import { useVirtualizer } from "@tanstack/react-virtual"
import { classNames } from "../../../../utils/classNames"

type Props<TRowData> = {
    style: TableStyle
    size: TableSize
    table: Table<TRowData>
    activeRowId?: Maybe<string>
    rowsHighlighting?: boolean
    bordered?: boolean
    renderSkeletons?: boolean
    rowClassName?: string
    virtualization?: TableVirtualizationConfig
    tableBodyWrapRef: React.RefObject<HTMLDivElement>
    onRowClick?: (data: TRowData) => void
    onRowClassName?: (data: TRowData, row: Row<TRowData>) => Maybe<string>
}

export const TableBody = <TRowData extends TableRowData>(props: Props<TRowData>) => {
    if (props.virtualization?.isEnabled) {
        return <TableBodyVirtualized {...props} />
    }

    return (
        <tbody className={classes.bodyWrap[props.style]}>
            {props.table.getRowModel().rows.map(row => (
                <TableBodyRow
                    key={row.id}
                    row={row}
                    size={props.size}
                    rowClassName={props.rowClassName}
                    onRowClick={props.onRowClick}
                    onRowClassName={props.onRowClassName}
                    activeRowId={props.activeRowId}
                    rowsHighlighting={props.rowsHighlighting}
                    bordered={props.bordered}>
                    {row.getVisibleCells().map(cell => {
                        return (
                            <TableBodyCell
                                table={props.table}
                                key={cell.id}
                                row={row}
                                cell={cell}
                                size={props.size}
                                renderSkeletons={!!props.renderSkeletons}
                            />
                        )
                    })}
                </TableBodyRow>
            ))}
        </tbody>
    )
}

const TableBodyVirtualized = <TRowData extends TableRowData>(props: Props<TRowData>) => {
    const { rows } = props.table.getRowModel()
    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: rows.length,
        estimateSize: () => props.virtualization?.rowHeight ?? 60, //estimate row height for accurate scrollbar dragging (todo: props?)
        getScrollElement: () => props.tableBodyWrapRef.current,
        //measure dynamic row height, except in firefox because it measures table border height incorrectly
        measureElement:
            typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
                ? element => element?.getBoundingClientRect().height
                : undefined,
        overscan: props.virtualization?.overscanCount ?? 5,
        enabled: props.virtualization?.isEnabled,
    })

    return (
        <tbody
            className={classNames(classes.bodyWrap[props.style], classes.bodyVirtualized)}
            style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
            }}>
            {rowVirtualizer.getVirtualItems().map(virtualRow => {
                const row = rows[virtualRow.index] as Row<TRowData>
                return (
                    <TableBodyRow
                        key={row.id}
                        row={row}
                        size={props.size}
                        rowClassName={props.rowClassName}
                        onRowClick={props.onRowClick}
                        onRowClassName={props.onRowClassName}
                        activeRowId={props.activeRowId}
                        rowsHighlighting={props.rowsHighlighting}
                        bordered={props.bordered}
                        virtualRow={virtualRow}
                        rowVirtualizer={rowVirtualizer}
                        virtualized={true}>
                        {row.getVisibleCells().map(cell => {
                            return (
                                <TableBodyCell
                                    table={props.table}
                                    key={cell.id}
                                    row={row}
                                    cell={cell}
                                    size={props.size}
                                    renderSkeletons={!!props.renderSkeletons}
                                    virtualized={true}
                                />
                            )
                        })}
                    </TableBodyRow>
                )
            })}
        </tbody>
    )
}

const classes = {
    bodyWrap: {
        normal: "divide-y divide-gray-200/80 border-t border-gray-200/80",
        dashed: "divide-y divide-gray-300/60 divide-dashed border-t border-gray-200/80",
        legacy: "divide-y divide-gray-200/80 border-t border-gray-200/80",
    },
    bodyVirtualized: "grid relative",
}
